import { formatClock, formatDayClock } from '../../lib/format.ts'
import { mechanismPresentation, severityPresentation } from '../../lib/detections/semantics.ts'
import { processHref } from '../../lib/logs/investigation.ts'
import { Link } from '../../lib/router.tsx'
import type { DetectionAlert } from '../../lib/sse/types.ts'
import { RelativeTime } from '../primitives/RelativeTime.tsx'
import { ResponseBadges } from './Response.tsx'
import { MitreChip, RiskMeter, SeverityBadge } from './Severity.tsx'

/**
 * A fila. Cada linha tem o trilho da severidade à esquerda, a regra como título
 * e o "onde/quem/o quê" embaixo — máquina, processo, usuário, linha de comando —,
 * porque é com isso que o analista decide abrir ou passar.
 */
export function DetectionList({
  alerts,
  selectedId,
  onOpen,
}: {
  alerts: readonly DetectionAlert[]
  selectedId: number | null
  onOpen: (alert: DetectionAlert) => void
}) {
  return (
    <ol className="flex flex-col">
      {alerts.map((alert, i) => (
        <DetectionRow
          key={alert.row.id ?? `${alert.row.received_at}:${i}`}
          alert={alert}
          selected={alert.row.id !== null && alert.row.id === selectedId}
          onOpen={() => onOpen(alert)}
        />
      ))}
    </ol>
  )
}

/** Uma linha. Exportada para o render check. */
export function DetectionRow({
  alert,
  selected = false,
  onOpen,
}: {
  alert: DetectionAlert
  selected?: boolean
  onOpen: () => void
}) {
  const p = severityPresentation(alert.severity)
  const mechanism = mechanismPresentation(alert.mechanism)
  const host = alert.row.hostname ?? alert.host_hostname ?? alert.row.endpoint_id.slice(0, 8)
  const received = Date.parse(alert.row.received_at)

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen()
          }
        }}
        aria-label={`Abrir alerta: ${alert.rule_name ?? alert.rule_id ?? 'alerta'}`}
        className={`group relative grid cursor-pointer grid-cols-[1fr_auto] gap-x-4 gap-y-1 border-b border-line-subtle py-2 pr-3 pl-4 hover:bg-inset ${
          selected ? 'bg-inset' : ''
        }`}
      >
        <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${p.fill}`} />

        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <SeverityBadge severity={alert.severity} />
          <span className="min-w-0 truncate text-base font-medium text-fg group-hover:text-accent">
            {alert.rule_name ?? alert.rule_id ?? 'Alerta sem regra'}
          </span>
          {mechanism !== null && alert.mechanism !== 'evento' && (
            <span className="border border-line px-1 text-micro uppercase tracking-label text-fg-muted" title={mechanism.hint}>
              {mechanism.label}
            </span>
          )}
          <ResponseBadges response={alert.response} />
        </span>

        <span className="flex items-center gap-3 justify-self-end">
          <RiskMeter score={alert.risk_score} compact />
          <span className="flex flex-col items-end leading-tight">
            <span className="tnum font-mono text-sm text-fg-secondary" title={`no endpoint · ${formatDayClock(alert.row.event_time)} UTC`}>
              {formatClock(alert.row.event_time)}
            </span>
            <RelativeTime
              since={Number.isFinite(received) ? received : null}
              intervalMs={15_000}
              className="text-micro text-fg-muted"
            />
          </span>
        </span>

        <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-micro text-fg-secondary">
          <span className="flex items-center gap-1">
            <span className="uppercase tracking-label text-fg-muted">host</span>
            <span className="font-mono text-fg">{host}</span>
          </span>
          {alert.process_name !== null && (
            <span className="flex items-center gap-1">
              <span className="uppercase tracking-label text-fg-muted">processo</span>
              {alert.process_entity_id !== null ? (
                <Link
                  href={processHref(alert.process_entity_id)}
                  onClick={(event) => event.stopPropagation()}
                  className="font-mono text-fg underline decoration-line-strong underline-offset-2 hover:text-accent"
                  title="Abrir a árvore e a linha do tempo do processo"
                >
                  {alert.process_name}
                </Link>
              ) : (
                <span className="font-mono text-fg">{alert.process_name}</span>
              )}
              {alert.process_pid !== null && <span className="tnum font-mono text-fg-muted">PID {alert.process_pid}</span>}
            </span>
          )}
          {alert.user_name !== null && (
            <span className="flex items-center gap-1">
              <span className="uppercase tracking-label text-fg-muted">usuário</span>
              <span className="font-mono text-fg">{alert.user_name}</span>
            </span>
          )}
          {alert.row.tenant_name !== null && <span className="text-fg-muted">{alert.row.tenant_name}</span>}
          <span className="ml-auto flex flex-wrap gap-1">
            {alert.techniques.slice(0, 3).map((technique) => (
              <MitreChip key={technique.id} reference={technique} quiet />
            ))}
            {alert.techniques.length > 3 && (
              <span className="text-micro text-fg-muted">+{alert.techniques.length - 3}</span>
            )}
          </span>
        </span>

        {alert.process_command_line !== null && (
          <span
            className="col-span-2 block truncate border-l-2 border-line-strong bg-inset px-1.5 py-0.5 font-mono text-micro text-fg-secondary"
            title={alert.process_command_line}
          >
            {alert.process_command_line}
          </span>
        )}
      </div>
    </li>
  )
}
